import React, { useState } from 'react';

/**
 * Interactive Brain Diagram Component
 *
 * A stylized side-view brain SVG with 5 clickable regions.
 * Each region highlights on hover and shows details on click.
 */
const BrainDiagram = ({
  activeRegion = null,
  onRegionClick = () => {},
  size = 'large',
  interactive = true,
  showLabels = true
}) => {
  const [hoveredRegion, setHoveredRegion] = useState(null);

  const sizes = {
    small: { width: 200, height: 160 },
    medium: { width: 300, height: 240 },
    large: { width: 400, height: 320 }
  };

  const { width, height } = sizes[size] || sizes.large;

  // Brain region definitions with SVG paths
  const regions = [
    {
      id: 'cerebrum',
      label: 'Cerebrum',
      color: '#1F93FE',
      // Main brain mass (simplified outline)
      path: 'M80,160 Q40,120 50,80 Q60,40 120,30 Q180,20 240,30 Q300,40 320,80 Q340,120 320,160 Q300,200 240,210 Q180,220 120,210 Q60,200 80,160 Z',
      labelPos: { x: 180, y: 50 }
    },
    {
      id: 'frontal',
      label: 'Frontal Lobe',
      color: '#667eea',
      // Front portion of the brain
      path: 'M80,160 Q40,120 50,80 Q60,40 120,30 Q150,25 170,35 L170,120 Q150,150 120,170 Q90,190 80,160 Z',
      labelPos: { x: 100, y: 90 }
    },
    {
      id: 'temporal',
      label: 'Temporal Lobe',
      color: '#764ba2',
      // Side/lower portion
      path: 'M80,160 Q90,190 120,200 Q160,210 200,200 L200,160 Q180,180 150,180 Q110,180 80,160 Z',
      labelPos: { x: 140, y: 190 }
    },
    {
      id: 'hippocampus',
      label: 'Hippocampus',
      color: '#10b981',
      // Deep structure (represented as small area)
      path: 'M150,150 Q165,140 180,145 Q195,150 200,165 Q195,180 180,180 Q165,175 155,165 Q150,155 150,150 Z',
      labelPos: { x: 175, y: 165 }
    },
    {
      id: 'amygdala',
      label: 'Amygdala',
      color: '#F7521B',
      // Small almond shape
      path: 'M120,155 Q130,145 145,150 Q155,158 150,170 Q140,175 130,170 Q120,165 120,155 Z',
      labelPos: { x: 135, y: 160 }
    }
  ];

  const handleClick = (regionId) => {
    if (interactive) {
      onRegionClick(regionId);
    }
  };

  const handleKeyDown = (e, regionId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(regionId);
    }
  };

  return (
    <svg
      className="brain-diagram"
      viewBox="0 0 400 280"
      width={width}
      height={height}
      role="img"
      aria-labelledby="brain-title brain-desc"
    >
      <title id="brain-title">Interactive Brain Anatomy Diagram</title>
      <desc id="brain-desc">
        A side view of the human brain with clickable regions:
        Cerebrum, Frontal Lobe, Temporal Lobe, Hippocampus, and Amygdala.
        Click or tap a region to learn about its function.
      </desc>

      {/* Background brain outline */}
      <defs>
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>

        <linearGradient id="brainGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="100%" stopColor="#e2e8f0" />
        </linearGradient>
      </defs>

      {/* Brain stem (simplified) */}
      <path
        d="M200,200 Q210,230 220,250 Q225,260 215,265 Q200,260 195,250 Q190,230 200,200"
        fill="#cbd5e1"
        stroke="#94a3b8"
        strokeWidth="1"
      />

      {/* Main brain regions */}
      {regions.map((region) => {
        const isActive = activeRegion === region.id;
        const isHovered = hoveredRegion === region.id;

        return (
          <g
            key={region.id}
            className={`brain-diagram__region ${isActive ? 'brain-diagram__region--active' : ''}`}
            onClick={() => handleClick(region.id)}
            onMouseEnter={() => setHoveredRegion(region.id)}
            onMouseLeave={() => setHoveredRegion(null)}
            onKeyDown={(e) => handleKeyDown(e, region.id)}
            tabIndex={interactive ? 0 : -1}
            role={interactive ? 'button' : 'img'}
            aria-label={`${region.label} region`}
            aria-pressed={isActive}
            style={{ cursor: interactive ? 'pointer' : 'default' }}
          >
            <path
              d={region.path}
              fill={region.color}
              fillOpacity={isActive || isHovered ? 1 : 0.85}
              stroke={isActive ? '#fff' : 'transparent'}
              strokeWidth={isActive ? 2 : 0}
              filter={isActive || isHovered ? 'url(#glow)' : 'none'}
              style={{
                transition: 'all 0.3s ease',
                transform: isActive ? 'scale(1.02)' : 'scale(1)',
                transformOrigin: 'center'
              }}
            />

            {showLabels && (
              <text
                x={region.labelPos.x}
                y={region.labelPos.y}
                className="brain-diagram__label"
                textAnchor="middle"
                fill={isActive || isHovered ? '#1A4072' : '#64748b'}
                fontWeight={isActive ? '700' : '600'}
                fontSize="10"
                style={{
                  pointerEvents: 'none',
                  transition: 'fill 0.2s ease'
                }}
              >
                {region.label.toUpperCase()}
              </text>
            )}
          </g>
        );
      })}

      {/* Decorative neurons/connections */}
      <g opacity="0.3" stroke="#1A4072" strokeWidth="0.5" fill="none">
        <circle cx="100" cy="100" r="3" fill="#1A4072" />
        <circle cx="250" cy="80" r="2" fill="#1A4072" />
        <circle cx="280" cy="140" r="2.5" fill="#1A4072" />
        <path d="M100,100 Q150,90 180,100" />
        <path d="M250,80 Q260,100 280,140" />
      </g>
    </svg>
  );
};

export default BrainDiagram;
